import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import reportWebVitals from './reportWebVitals';
import PullToRefresh from 'pulltorefreshjs';
import './index.css';

const standalone = window.matchMedia('(display-mode: standalone)').matches;

// ✅ Reload safety flag
let hasReloaded = false;
const safeReload = () => {
  if (hasReloaded) return;
  hasReloaded = true;
  console.log('♻️ Reloading app...');
  window.location.reload();
};

const isIOS = () => {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
};

// ✅ Pull to refresh for iOS standalone
if (standalone && isIOS()) {
  PullToRefresh.init({
    onRefresh() {
      safeReload();
    },
  });
}

// ✅ Version checker
const checkForNewVersion = async () => {
  try {
    const response = await fetch('/version.json?v=' + Date.now(), {
      cache: 'no-store',
    });
    const data = await response.json();
    const currentVersion = data.version;
    const storedVersion = localStorage.getItem('app_version');

    if (storedVersion && storedVersion !== currentVersion) {
      console.log('🆕 New version detected. Refreshing...');

      // Clear everything (SW cache, LS, cookies)
      await clearServiceWorkerCaches();

      localStorage.setItem('app_version', currentVersion);
      window.location.reload(); // force reload
    } else {
      localStorage.setItem('app_version', currentVersion);
    }
  } catch (error) {
    console.error('❌ Version check failed:', error);
  }
};

const clearServiceWorkerCaches = async () => {
  // Unregister all SWs
  const regs = await navigator.serviceWorker.getRegistrations();
  for (const reg of regs) await reg.unregister();

  // Delete all caches
  const keys = await caches.keys();
  await Promise.all(keys.map((key) => caches.delete(key)));

  // Clear cookies
  document.cookie.split(';').forEach((c) => {
    document.cookie = c
      .replace(/^ +/, '')
      .replace(/=.*/, '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/');
  });

  // ✅ Preserve important user data before clearing localStorage
  const token = localStorage.getItem('token');
  const userPreferences = localStorage.getItem('userPreferences');

  localStorage.clear();

  // ✅ Restore preserved data
  if (token) localStorage.setItem('token', token);
  if (userPreferences) localStorage.setItem('userPreferences', userPreferences);
};

// ✅ Check version before app loads
window.onload = () => {
  checkForNewVersion(); // 🆕 always check on launch
};

// ✅ Register service worker with SKIP_WAITING
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .then((registration) => {
        console.log(
          '✅ ServiceWorker registered with scope:',
          registration.scope,
        );

        registration.onupdatefound = () => {
          const installingWorker = registration.installing;
          if (installingWorker) {
            installingWorker.onstatechange = () => {
              if (
                installingWorker.state === 'installed' &&
                navigator.serviceWorker.controller
              ) {
                console.log('🚀 New service worker installed. Activating...');
                installingWorker.postMessage({ type: 'SKIP_WAITING' });
              }
            };
          }
        };
      })
      .catch((error) => {
        console.log('❌ ServiceWorker registration failed:', error);
      });

    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('🔁 SW controller changed. Reloading...');
      safeReload();
    });
  });
}

// ✅ Mount app
const root = createRoot(document.getElementById('root'));
root.render(<App />);

// 📈 Optional performance monitoring
reportWebVitals();
